import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  PauseCircle,
  RefreshCw,
  Loader2,
  CreditCard,
  Search,
  Play,
  Pause,
  Plus,
} from 'lucide-react'
import { ApiService, type ClientSubscriptionItem } from '@/services/api'
import { formatDate } from '@/lib/utils'

// ── helpers ──────────────────────────────────────────────────────────────────

const PLAN_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  halfyearly: '6 Months',
  yearly: '1 Year',
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

function planMonths(plan: string): number {
  return plan === 'monthly' ? 1 : plan === 'halfyearly' ? 6 : 12
}

function toInputDate(date: Date | string | null): string {
  if (!date) return ''
  return new Date(date).toISOString().split('T')[0]
}

function statusBadge(item: ClientSubscriptionItem) {
  switch (item.status) {
    case 'active':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200 gap-1">
          <CheckCircle className="h-3 w-3" /> Active
        </Badge>
      )
    case 'expiring':
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
          <AlertTriangle className="h-3 w-3" /> Expires in {item.daysRemaining}d
        </Badge>
      )
    case 'expired':
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200 gap-1">
          <XCircle className="h-3 w-3" /> Expired
        </Badge>
      )
    case 'suspended':
      return (
        <Badge className="bg-gray-100 text-gray-600 border-gray-200 gap-1">
          <PauseCircle className="h-3 w-3" /> Suspended
        </Badge>
      )
    default:
      return <Badge variant="outline">Unknown</Badge>
  }
}

// ── types ─────────────────────────────────────────────────────────────────────

interface FormState {
  clientDatabaseName: string
  restaurantName: string
  plan: 'monthly' | 'halfyearly' | 'yearly'
  startDate: string
  expiryDate: string
  isActive: boolean
}

const DB_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]{2,49}$/

// ── component ─────────────────────────────────────────────────────────────────

export default function SubscriptionManagementScreen() {
  const [items, setItems] = useState<ClientSubscriptionItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // Manage modal
  const [manageTarget, setManageTarget] = useState<ClientSubscriptionItem | null>(null)
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [form, setForm] = useState<FormState>({
    clientDatabaseName: '',
    restaurantName: '',
    plan: 'yearly',
    startDate: toInputDate(new Date()),
    expiryDate: toInputDate(addMonths(new Date(), 12)),
    isActive: true,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const isModalOpen = !!manageTarget || isCreatingNew
  const closeModal = () => {
    setManageTarget(null)
    setIsCreatingNew(false)
  }

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await ApiService.getAllClientSubscriptions()
      if (res.success) setItems(res.data)
      else setError((res as any).message || 'Failed to load')
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load subscriptions')
    } finally {
      setIsLoading(false)
    }
  }

  // ── form helpers ────────────────────────────────────────────────────────────

  const recalcExpiry = (plan: string, start: string) => {
    if (!start) return ''
    return toInputDate(addMonths(new Date(start), planMonths(plan)))
  }

  const openManage = (item: ClientSubscriptionItem) => {
    setManageTarget(item)
    setIsCreatingNew(false)
    setSaveError(null)

    const today = toInputDate(new Date())

    if (item.hasSubscription && item.expiryDate) {
      // Renewal: extend from current expiry
      const currentExpiry = item.expiryDate
      const plan = item.plan || 'yearly'
      setForm({
        clientDatabaseName: item.clientDatabaseName,
        restaurantName: item.restaurantName,
        plan,
        startDate: currentExpiry.split('T')[0],
        expiryDate: toInputDate(addMonths(new Date(currentExpiry), planMonths(plan))),
        isActive: item.isActive,
      })
    } else {
      // New subscription for an existing restaurant row
      setForm({
        clientDatabaseName: item.clientDatabaseName,
        restaurantName: item.restaurantName,
        plan: 'yearly',
        startDate: today,
        expiryDate: toInputDate(addMonths(new Date(today), 12)),
        isActive: true,
      })
    }
  }

  const openCreateNew = () => {
    setManageTarget(null)
    setIsCreatingNew(true)
    setSaveError(null)
    const today = toInputDate(new Date())
    setForm({
      clientDatabaseName: '',
      restaurantName: '',
      plan: 'yearly',
      startDate: today,
      expiryDate: toInputDate(addMonths(new Date(today), 12)),
      isActive: true,
    })
  }

  const handlePlanChange = (plan: 'monthly' | 'halfyearly' | 'yearly') => {
    const expiry = recalcExpiry(plan, form.startDate)
    setForm(f => ({ ...f, plan, expiryDate: expiry }))
  }

  const handleStartDateChange = (startDate: string) => {
    const expiry = recalcExpiry(form.plan, startDate)
    setForm(f => ({ ...f, startDate, expiryDate: expiry }))
  }

  // ── actions ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!manageTarget && !isCreatingNew) return
    if (!form.startDate || !form.expiryDate) {
      setSaveError('Start date and expiry date are required')
      return
    }

    const clientDatabaseName = isCreatingNew ? form.clientDatabaseName.trim() : manageTarget!.clientDatabaseName
    const restaurantName = isCreatingNew ? form.restaurantName.trim() : manageTarget!.restaurantName

    if (isCreatingNew) {
      if (!restaurantName) {
        setSaveError('Restaurant name is required')
        return
      }
      if (!DB_NAME_PATTERN.test(clientDatabaseName)) {
        setSaveError('Database name must start with a letter and contain only letters, numbers, hyphens, or underscores (3–50 characters)')
        return
      }
    }

    setIsSaving(true)
    setSaveError(null)
    try {
      const res = await ApiService.saveClientSubscription({
        clientDatabaseName,
        restaurantName,
        plan: form.plan,
        startDate: form.startDate,
        expiryDate: form.expiryDate,
        isActive: form.isActive,
      })
      if (res.success) {
        closeModal()
        await loadData()
      } else {
        setSaveError((res as any).message || 'Save failed')
      }
    } catch (e: any) {
      setSaveError(e.response?.data?.message || 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSuspend = async (dbName: string) => {
    try {
      await ApiService.suspendClientSubscription(dbName)
      await loadData()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to suspend')
    }
  }

  const handleActivate = async (dbName: string) => {
    try {
      await ApiService.activateClientSubscription(dbName)
      await loadData()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to activate')
    }
  }

  // ── derived ──────────────────────────────────────────────────────────────────

  const filtered = items.filter(i =>
    i.restaurantName.toLowerCase().includes(search.toLowerCase()) ||
    i.clientDatabaseName.toLowerCase().includes(search.toLowerCase())
  )

  const stats = {
    active: items.filter(i => i.status === 'active').length,
    expiring: items.filter(i => i.status === 'expiring').length,
    expired: items.filter(i => i.status === 'expired').length,
    suspended: items.filter(i => i.status === 'suspended').length,
  }

  // ── render ───────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Subscriptions</h1>
          <p className="text-muted-foreground mt-1">Manage client device subscriptions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={openCreateNew}>
            <Plus className="mr-2 h-4 w-4" />
            New Client
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        {[
          { label: 'Active', value: stats.active, color: 'text-green-600' },
          { label: 'Expiring Soon', value: stats.expiring, color: 'text-amber-600' },
          { label: 'Expired', value: stats.expired, color: 'text-red-600' },
          { label: 'Suspended', value: stats.suspended, color: 'text-gray-500' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search clients..."
          className="pl-9"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Client list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-4 w-4" />
            Client Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left px-4 py-3 font-medium">Client</th>
                  <th className="text-left px-4 py-3 font-medium">Plan</th>
                  <th className="text-left px-4 py-3 font-medium">Expiry</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-muted-foreground">
                      {search ? 'No clients match your search' : 'No clients found'}
                    </td>
                  </tr>
                ) : (
                  filtered.map(item => (
                    <tr key={item.clientDatabaseName} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.restaurantName}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {item.clientDatabaseName}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {item.hasSubscription
                          ? PLAN_LABELS[item.plan] || item.plan
                          : <span className="text-muted-foreground italic">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {item.expiryDate
                          ? formatDate(item.expiryDate)
                          : <span className="text-muted-foreground italic">—</span>}
                      </td>
                      <td className="px-4 py-3">{statusBadge(item)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openManage(item)}
                          >
                            {item.hasSubscription ? (
                              item.status === 'expired' ? 'Renew' : 'Manage'
                            ) : 'New Subscription'}
                          </Button>
                          {item.hasSubscription && item.status !== 'suspended' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-600 hover:text-amber-700"
                              onClick={() => handleSuspend(item.clientDatabaseName)}
                            >
                              <Pause className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {item.status === 'suspended' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleActivate(item.clientDatabaseName)}
                            >
                              <Play className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Manage / New Subscription / New Client Modal */}
      <Dialog open={isModalOpen} onOpenChange={open => !open && closeModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isCreatingNew
                ? 'New Client'
                : `${manageTarget?.hasSubscription ? 'Subscription' : 'New Subscription'} — ${manageTarget?.restaurantName}`}
            </DialogTitle>
            {manageTarget && !isCreatingNew && (
              <p className="text-sm text-muted-foreground font-mono">
                {manageTarget.clientDatabaseName}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-5 py-2">
            {saveError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            )}

            {/* New client identity */}
            {isCreatingNew && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="restaurantName">Restaurant Name</Label>
                  <Input
                    id="restaurantName"
                    placeholder="e.g. Chai Bar"
                    value={form.restaurantName}
                    onChange={e => setForm(f => ({ ...f, restaurantName: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientDatabaseName">Client Database Name</Label>
                  <Input
                    id="clientDatabaseName"
                    placeholder="e.g. chai-bar"
                    className="font-mono"
                    value={form.clientDatabaseName}
                    onChange={e => setForm(f => ({ ...f, clientDatabaseName: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be unique — this is what the client device is provisioned with. Letters, numbers, hyphens, underscores only.
                  </p>
                </div>
              </div>
            )}

            {/* Plan */}
            <div className="space-y-2">
              <Label>Plan</Label>
              <div className="flex gap-3">
                {(['monthly', 'halfyearly', 'yearly'] as const).map(p => (
                  <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="plan"
                      value={p}
                      checked={form.plan === p}
                      onChange={() => handlePlanChange(p)}
                      className="accent-primary"
                    />
                    <span className="text-sm">{PLAN_LABELS[p]}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">
                  Start Date
                  {manageTarget?.hasSubscription && manageTarget.status !== 'expired' && (
                    <span className="ml-1 text-xs text-muted-foreground">(extends from expiry)</span>
                  )}
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={form.startDate}
                  onChange={e => handleStartDateChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={form.expiryDate}
                  onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))}
                />
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={form.isActive}
                    onChange={() => setForm(f => ({ ...f, isActive: true }))}
                    className="accent-primary"
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={!form.isActive}
                    onChange={() => setForm(f => ({ ...f, isActive: false }))}
                    className="accent-primary"
                  />
                  <span className="text-sm">Suspended</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isCreatingNew ? 'Create Client' : 'Save Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
